import { useMemo } from 'react';

type GetId<T> = (item: T) => string;
type GetLabel<T> = (item: T) => string;
type GetHasNextPage = () => boolean;

type UseReorderableStarredItemsArgs<T> = {
	favourites: T[];
	lastOrder:
		| readonly (
				| { menuId: string | null | undefined; visible: boolean | null | undefined }
				| null
				| undefined
		  )[]
		| null
		| undefined;
	getId: GetId<T>;
	getHasNextPage?: GetHasNextPage;
	getLabel: GetLabel<T>;
	maxItems: number;
	renderMenuItem: (
		edge: T,
		currentItems: Array<{ id: string; label: string }>,
		currentOrder: Array<{ menuId: string; visible: boolean }>,
		item: { id: string; label: string },
	) => React.JSX.Element;
};
type UseReorderableStarredItemsReturn = {
	currentItems: Array<{ id: string; label: string }>;
	currentOrder: Array<{ menuId: string; visible: boolean }>;
	list: React.JSX.Element[];
	idSet: Set<string>;
	hasItems: boolean;
	hasNextPage: boolean;
};

export function useReorderableStarredItems<T>({
	favourites,
	lastOrder,
	getId,
	getLabel,
	getHasNextPage,
	maxItems,
	renderMenuItem,
}: UseReorderableStarredItemsArgs<T>): UseReorderableStarredItemsReturn {
	const reorderableStarredItemData = useMemo(() => {
		const ids = favourites.map((favourite) => getId(favourite)).filter(Boolean) ?? [];

		const preferredItemOrder =
			lastOrder
				?.map((node) => ({
					menuId: node?.menuId || '',
					visible: node?.visible || false,
				}))
				.filter((item) => item.menuId) || [];

		const preferredItemIds = preferredItemOrder.map((item) => item.menuId);

		// Filter out any previously ordered items that are no longer available...
		const filteredPreferredItemOrder = preferredItemOrder.filter((item) =>
			ids.includes(item.menuId),
		);

		// Find any available items that weren't in the starred list (these will get added to the end)
		const newItemIds = ids.filter((id) => !preferredItemIds.includes(id));

		// Combine the two to get the final order
		const finalItemOrderIds = [
			...filteredPreferredItemOrder.map((item) => item.menuId),
			...newItemIds,
		];

		const list: React.JSX.Element[] = [];
		const currentItems: Array<{ id: string; label: string }> = [];
		finalItemOrderIds.forEach((itemId) => {
			const itemEdge = favourites.find((edge) => getId(edge) === itemId);
			if (itemEdge) {
				const item = {
					id: getId(itemEdge) || '',
					label: getLabel(itemEdge) || '',
				};
				currentItems.push(item);
				getId(itemEdge) &&
					list.push(renderMenuItem(itemEdge, currentItems, preferredItemOrder, item));
			}
		});

		return {
			idSet: new Set(finalItemOrderIds),
			list: list.slice(0, maxItems),
			hasItems: Boolean(list.length),
			hasNextPage: (getHasNextPage && getHasNextPage()) || list.length > maxItems,
			currentOrder: preferredItemOrder,
			currentItems,
		};
	}, [favourites, lastOrder, getHasNextPage, getId, getLabel, renderMenuItem, maxItems]);
	return reorderableStarredItemData;
}
